import React, { useEffect, useState, useMemo } from "react";
import "./Mindfulness.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faChevronLeft,
  faChevronRight,
  faArrowUpRightFromSquare,
  faXmark,
  faMagnifyingGlass,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMindfulnessExercisesThunk,
  selectMindfulnessExercises,
  selectMindfulnessCategories,
  selectMindfulnessFavourites,
  selectMindfulnessLoading,
  selectMindfulnessInitialLoaded,
  selectMindfulnessIsRevalidating,
} from "../../../store/mindfulnessSlice";
import { MainHeader } from "../../chatMain/MainHeader/MainHeader.jsx";
import { Exercise } from "../ExerciseCard/ExerciseCard.jsx";
import { SidebarCol } from "../../chatMain/Sidebar/Sidebar.jsx";
import { MindfulnessMusicBar } from "../../../components/common/MindfulnessMusicBar/MindfulnessMusicBar";

const categories = [
  "improve mood",
  "increase focus",
  "improve sleep",
  "relaxation",
  "improve communication",
  "attract love",
  "forgiveness",
  "gratitude",
  "selfcare",
  "reduce stress & anxiety",
  "build trust",
  "reduce stress at work",
  "balance emotions",
  "be present",
  "financial wellness",
];

export const Mindfulness = () => {
  const dispatch = useDispatch();

  // Redux cached state: instant on return visits, updated silently in background
  const exercises = useSelector(selectMindfulnessExercises);
  const mindfulnessCategory = useSelector(selectMindfulnessCategories);
  const userFavourites = useSelector(selectMindfulnessFavourites);
  const loading = useSelector(selectMindfulnessLoading);
  const initialLoaded = useSelector(selectMindfulnessInitialLoaded);
  const isRevalidating = useSelector(selectMindfulnessIsRevalidating);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  const [showExternalOnly, setShowExternalOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const scrollCategoryLeft = () => {
    const categoriesDiv = document.getElementById("categories");
    if (categoriesDiv) {
      categoriesDiv.scrollBy({ left: -220, behavior: "smooth" });
    }
  };

  const scrollCategoryRight = () => {
    const categoriesDiv = document.getElementById("categories");
    if (categoriesDiv) {
      categoriesDiv.scrollBy({ left: 220, behavior: "smooth" });
    }
  };

  useEffect(() => {
    // If first time visit: fetch with loader
    // If already visited and in Redux: render instantly and revalidate silently in background
    if (!initialLoaded) {
      dispatch(fetchMindfulnessExercisesThunk({ silent: false }));
    } else {
      dispatch(fetchMindfulnessExercisesThunk({ silent: true }));
    }
  }, [dispatch, initialLoaded]);

  const favSet = useMemo(() => {
    return new Set(
      userFavourites.map((f) =>
        typeof f === "string" ? f.trim().toLowerCase() : ""
      )
    );
  }, [userFavourites]);

  const filteredExercises = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return exercises.filter((exercise) => {
      if (!exercise || !exercise.title) return false;

      const titleRaw = (exercise.title || "").trim();
      const titleLower = titleRaw.toLowerCase();
      const summaryLower = (exercise.summary || "").toLowerCase();
      const categoryLower = (exercise.category || "").toLowerCase();
      const externalCategoryLower = (
        exercise.externalCategory || ""
      ).toLowerCase();
      const mappedCategories = mindfulnessCategory[titleLower] || [];

      // KAN-116: Favourites filter
      if (showFavouritesOnly && !favSet.has(titleLower)) {
        return false;
      }

      // KAN-119: External guidance filter
      if (
        showExternalOnly &&
        exercise.local !== "no" &&
        categoryLower !== "external"
      ) {
        return false;
      }

      // Category filter
      if (selectedCategory) {
        const targetCategory = selectedCategory.trim().toLowerCase();
        const matchesMapped = mappedCategories.some((c) =>
          c.includes(targetCategory)
        );
        const matchesExternal = externalCategoryLower.includes(targetCategory);
        const matchesDirect = categoryLower.includes(targetCategory);
        if (!matchesMapped && !matchesExternal && !matchesDirect) {
          return false;
        }
      }

      // KAN-117: Search query filter (matches title, summary, category, externalCategory, and mapped categories from guidanceTypes.csv)
      if (query) {
        const matchesTitle = titleLower.includes(query);
        const matchesSummary = summaryLower.includes(query);
        const matchesCategory = categoryLower.includes(query);
        const matchesExternal = externalCategoryLower.includes(query);
        const matchesMapped = mappedCategories.some((c) => c.includes(query));

        if (
          !matchesTitle &&
          !matchesSummary &&
          !matchesCategory &&
          !matchesExternal &&
          !matchesMapped
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    exercises,
    searchQuery,
    selectedCategory,
    showFavouritesOnly,
    showExternalOnly,
    favSet,
    mindfulnessCategory,
  ]);

  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
    }
  };

  const handleFavouriteToggle = () => {
    setShowFavouritesOnly((prev) => !prev);
  };

  const handleExternalToggle = () => {
    setShowExternalOnly((prev) => !prev);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedCategory(null);
    setShowFavouritesOnly(false);
    setShowExternalOnly(false);
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(selectedCategory) ||
    showFavouritesOnly ||
    showExternalOnly;

  return (
    <div className="mindfulness-page">
      <div className="sidebar">
        <SidebarCol />
      </div>
      <div className="mindfulness-container-wrapper">
        <div className="mindfulness-header">
          <MainHeader
            page={"mindfulness"}
            titleText={"Mindfulness Practices"}
            subtitleText={
              "Find focus, reduce stress, and stay present with simple daily practices."
            }
            searchValue={searchQuery}
            onSearchChange={(val) => setSearchQuery(val)}
            searchPlaceholder="Search practices..."
          />
        </div>

        <div className="mindfulness-content-container">
          <div className="mindfulness-content-wrapper">
            {/* Soothing Ambient Background Music Player Bar */}
            <MindfulnessMusicBar />

            <div className="mindfulness-toolbar">
              {/* Mobile Search Bar (displayed on screens <= 768px when header search collapses) */}
              <div className="mindfulness-mobile-search">
                <div className="mobile-search-input-wrapper">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="mobile-search-icon"
                  />
                  <input
                    type="text"
                    placeholder="Search practices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mobile-search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="mobile-search-clear"
                      title="Clear search"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
              </div>

              {/* Primary Toggle Filters (Favourites, External Guidance) + Count & Reset */}
              <div className="mindfulness-primary-filters">
                <div className="primary-filters-group">
                  <button
                    type="button"
                    className={`filter-pill ${showFavouritesOnly ? "active" : ""}`}
                    onClick={handleFavouriteToggle}
                    title="Filter by your saved favourites"
                  >
                    <FontAwesomeIcon
                      icon={faStar}
                      className="pill-icon fav-star"
                    />
                    <span>Favourites</span>
                    {userFavourites.length > 0 && (
                      <span className="pill-badge">{userFavourites.length}</span>
                    )}
                  </button>

                  <button
                    type="button"
                    className={`filter-pill ${showExternalOnly ? "active" : ""}`}
                    onClick={handleExternalToggle}
                    title="Filter by external guidance practices"
                  >
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare}
                      className="pill-icon"
                    />
                    <span>External Guidance</span>
                  </button>
                </div>

                <div className="mindfulness-results-info">
                  <span className="results-count">
                    {loading && !initialLoaded ? (
                      <span className="mindfulness-loading-badge">
                        <FontAwesomeIcon icon={faSpinner} spin className="count-spinner" />
                        Loading practices...
                      </span>
                    ) : (
                      <>
                        <strong>{filteredExercises.length}</strong>{" "}
                        {filteredExercises.length === 1 ? "practice" : "practices"}
                      </>
                    )}
                  </span>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      className="reset-filters-link"
                      onClick={clearAllFilters}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        style={{ marginRight: "4px" }}
                      />
                      Reset filters
                    </button>
                  )}
                </div>
              </div>

              {/* Category Pills Horizontal Track */}
              <div className="mindfulness-category-scroller">
                <button
                  type="button"
                  className="scroll-nav-btn"
                  onClick={scrollCategoryLeft}
                  title="Scroll categories left"
                  aria-label="Scroll left"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <div className="categories-track" id="categories">
                  <button
                    type="button"
                    className={`category-chip ${selectedCategory === null ? "active" : ""}`}
                    onClick={() => setSelectedCategory(null)}
                  >
                    All Practices
                  </button>

                  {categories.map((category) => {
                    const isSelected = selectedCategory === category;
                    return (
                      <button
                        key={category}
                        type="button"
                        className={`category-chip ${isSelected ? "active" : ""}`}
                        onClick={() => handleCategoryClick(category)}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="scroll-nav-btn"
                  onClick={scrollCategoryRight}
                  title="Scroll categories right"
                  aria-label="Scroll right"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>

            {/* Practices Grid */}
            <div className="content-section-container">
              {loading && !initialLoaded ? (
                <>
                  <div className="mindfulness-loader-banner">
                    <div className="mindfulness-spinner-ring"></div>
                    <p className="mindfulness-loader-text">Loading mindfulness practices...</p>
                  </div>
                  <div className="content-section-row">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <div key={n} className="exercise-card-skeleton">
                        <div className="skeleton-media shimmer"></div>
                        <div className="skeleton-content">
                          <div className="skeleton-tag shimmer"></div>
                          <div className="skeleton-title shimmer"></div>
                          <div className="skeleton-desc shimmer"></div>
                          <div className="skeleton-desc short shimmer"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="content-section-row">
                  {filteredExercises.length === 0 ? (
                  <div className="mindfulness-empty-state">
                    <div className="mindfulness-empty-icon">
                      <FontAwesomeIcon
                        icon={showFavouritesOnly ? faStar : faMagnifyingGlass}
                      />
                    </div>
                    <div className="mindfulness-empty-title">
                      {showFavouritesOnly
                        ? "No favourite practices found"
                        : "No matching practices found"}
                    </div>
                    <div className="mindfulness-empty-desc">
                      {showFavouritesOnly
                        ? "You haven't added any practices to your favourites yet, or none match the active filters."
                        : `We couldn't find any practices matching your search${
                            searchQuery ? ` "${searchQuery}"` : ""
                          }. Try checking for typos or clearing active filters.`}
                    </div>
                    {hasActiveFilters && (
                      <button
                        type="button"
                        className="clear-filter-btn"
                        onClick={clearAllFilters}
                      >
                        <FontAwesomeIcon
                          icon={faXmark}
                          style={{ marginRight: "6px" }}
                        />
                        Clear all filters
                      </button>
                    )}
                  </div>
                ) : (
                  filteredExercises.map((exercise, index) => {
                    const titleKey = (exercise.title || "").trim();
                    const isFav = favSet.has(titleKey.toLowerCase());
                    return (
                      <Exercise
                        key={exercise.defaulturl || titleKey || index}
                        categoryType={
                          exercise.local === "no" && exercise.externalCategory
                            ? exercise.externalCategory
                            : exercise.category
                        }
                        imageurl={exercise.imageurl}
                        titleText={titleKey}
                        exerciseURL={exercise.defaulturl}
                        summaryText={exercise.summary}
                        internal={exercise.local}
                        exerciseDuration={exercise.duration}
                        isFavourited={isFav}
                        creator={exercise.creator}
                        courtesy={exercise.courtesy}
                      />
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);
};
